import { ArrowRight, Droplets, PartyPopper, Leaf } from "lucide-react";
import { eventServices, cleaningServices, gardenServices } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "./Reveal";

const tones = {
  cleaning: { panel: "bg-[#eaf3f9]", title: "text-[#2f6b94]", iconWrap: "bg-[#2f6b94]", link: "text-[#2f6b94]" },
  events: { panel: "bg-[#fdeef2]", title: "text-[#d64a63]", iconWrap: "bg-[#E85D75]", link: "text-[#E85D75]" },
  garden: { panel: "bg-[#ecf5ee]", title: "text-[#3c6749]", iconWrap: "bg-[#4A7C59]", link: "text-[#4A7C59]" },
};

const Panel = ({ tone, icon: Icon, title, items, viewAllHref, sectionKey, onTile, L, t }) => {
  const c = tones[tone];
  return (
    <div className={`rounded-3xl ${c.panel} p-4 sm:p-6`} data-testid={`board-panel-${sectionKey}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className={`flex items-center gap-2.5 text-xl sm:text-3xl font-bold tracking-tight ${c.title}`}>
          <span className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${c.iconWrap} text-white flex items-center justify-center shrink-0`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </span>
          {title}
        </h2>
        <a
          href={viewAllHref}
          data-testid={`board-viewall-${sectionKey}`}
          className={`shrink-0 inline-flex items-center gap-1 text-sm font-semibold ${c.link} hover:underline`}
        >
          {t.viewAll} <ArrowRight className="w-4 h-4" />
        </a>
      </div>
      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
        {items.map((s) => (
          <button
            key={s.id}
            onClick={() => onTile(sectionKey, s)}
            data-testid={`board-tile-${s.id}`}
            className="snap-start shrink-0 w-28 sm:w-36 group text-center"
          >
            <div className="rounded-xl overflow-hidden aspect-[4/5] bg-white shadow-sm group-hover:shadow-md group-hover:-translate-y-0.5 transition-all duration-200">
              <img
                src={s.image}
                alt={s.name}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <p className="mt-2 text-sm sm:text-base font-semibold text-stone-700 leading-tight">
              {L(s, "name")}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ServiceBoard = ({ onTile }) => {
  const { t, L } = useLanguage();
  return (
    <section id="home" className="pt-24 sm:pt-28 pb-14 sm:pb-20" data-testid="service-board">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-5 sm:space-y-6">
        <Reveal>
          <Panel tone="garden" icon={Leaf} title={t.chapters.garden.title} items={gardenServices} viewAllHref="#garden" sectionKey="garden" onTile={onTile} L={L} t={t} />
        </Reveal>
        <Reveal delay={0.08}>
          <Panel tone="events" icon={PartyPopper} title={t.chapters.events.title} items={eventServices} viewAllHref="#events" sectionKey="events" onTile={onTile} L={L} t={t} />
        </Reveal>
        <Reveal delay={0.16}>
          <Panel tone="cleaning" icon={Droplets} title={t.chapters.cleaning.title} items={cleaningServices} viewAllHref="#cleaning" sectionKey="cleaning" onTile={onTile} L={L} t={t} />
        </Reveal>
      </div>
    </section>
  );
};
